/**
 * English: Login form — Agent Station session (username/password only); lands on Dashboard at /.
 * Thai: ฟอร์ม login — session Agent Station (username/password); ไป Dashboard ที่ /
 */
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { safeReturnPath } from '../config/publicPath';
import LoadingSpinner from '../components/shared/LoadingSpinner';
import Button from '../components/shared/Button';
import Input from '../components/shared/Input';
import Card from '../components/shared/Card';

export default function LoginPage() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim() || !password) {
      setError(t('login.fillUsernamePassword'));
      return;
    }
    setLoading(true);
    setError('');
    try {
      const result = await login(username.trim(), password);
      if (!result.ok) {
        setError(result.error || t('login.unauthorized'));
        return;
      }
      navigate(safeReturnPath(params.get('returnTo')), { replace: true });
    } catch (err) {
      setError((err as Error).message || t('login.networkError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-canvas flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="w-12 h-12 bg-primary rounded-lg mx-auto mb-3 flex items-center justify-center">
            <span className="text-on-primary text-xl font-bold">A</span>
          </div>
          <h1 className="text-display-md font-semibold text-ink">{t('login.title')}</h1>
          <p className="mt-2 text-body-md text-muted">{t('login.subtitle')}</p>
        </div>

        <Card>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="rounded-lg border border-semantic-down/30 bg-semantic-down/10 px-4 py-3">
                <p className="text-body-sm text-semantic-down">{error}</p>
              </div>
            )}

            <Input
              label={t('login.username')}
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder={t('login.usernamePlaceholder')}
              autoFocus
            />

            <Input
              label={t('login.password')}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('login.passwordPlaceholder')}
            />

            <Button type="submit" disabled={loading} className="w-full">
              {loading ? <LoadingSpinner size="sm" /> : t('login.signIn')}
            </Button>
          </form>
        </Card>

        <p className="text-caption text-muted text-center mt-4">{t('login.footer')}</p>
      </div>
    </div>
  );
}
